import React from "react";
import PropTypes from "prop-types";

import { Box, Grid, Typography, withStyles } from "@material-ui/core";

import RadioButton from "components/portal/shared/selectors/radio_button";
import SingleSelectCalendar from "components/portal/shared/calendars/single_select_calendar";
import SingleSelectDropdown from "components/shared/inputs/single_select_dropdown";
import SelectActivity from "components/portal/booking_management/enrollment_form/select_activity";
import Notes from "components/portal/booking_management/enrollment_form/notes";
import StudentAlreadyBookedAlert from "components/portal/booking_management/enrollment_form/student_already_booked_alert";
import Alert from "components/marketplace/shared/alert.jsx";
import moment from "moment";
import { isDesktop } from "components/marketplace/shared/media_queries";

const styles = (theme) => ({
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  titleWithSubtitle: {
    marginBottom: theme.spacing(0.5),
  },
  subTitle: {
    marginBottom: theme.spacing(2),
  },
  noteInput: {
    marginTop: theme.spacing(2),
  },
  dateRoot: {
    width: "100%",
    "&& .MuiInputBase-root.Mui-error": {
      borderWidth: "2px",
    },
  },
  radioButton: {
    "@media (min-width: 1025px)": {
      marginLeft: theme.spacing(0.75),
    },
  },
  searchBy: {
    marginBottom: theme.spacing(0.25),
    "@media (max-width: 1024px)": {
      marginBottom: 0,
      marginTop: theme.spacing(0.5),
    },
  },
  capacityAlert: {
    marginTop: theme.spacing(2),
  },
});

const SingleDayEnrollmentForm = (props) => {
  const {
    classes,
    activities,
    activitySessionGroupId,
    availableDatesDropdown,
    availableDatesCalendar,
    onSelectActivitySessionGroupId,
    onSelectStartDate,
    selectedStartDate,
    maxDateForCalendar,
    minDateForCalendar,
    onOptionMouseLeave,
    onOptionMouseEnter,
    onNotesChange,
    setSearchByField,
    searchBy,
    internalNotes,
    errors,
    setErrors,
    loadingActivities,
    studentAlreadyBooked,
    terminology,
  } = props;

  const isDesktopLayout = isDesktop();

  const selectedRadio = searchBy;
  const switchRadio = (value) => {
    setSearchByField(value);
    onSelectActivitySessionGroupId(null);
    onSelectStartDate("");
    setErrors({});
  };

  const handleActivityChange = (value) => {
    onSelectActivitySessionGroupId(value);
  };

  const featureFlags =
    document.getElementsByClassName("feature-flag-data")?.[0]?.dataset || {};

  const showCapacity = () => {
    const session = availableDatesDropdown.find(
      (sess) => sess.date === selectedStartDate
    );

    return (
      session && (
        <Alert
          content={session.searchByDateCapacityMessage}
          severity="info"
          className={classes.capacityAlert}
        />
      )
    );
  };

  const createDateSelector = () => {
    const scrollToOptionIndex = availableDatesDropdown.findIndex((item) =>
      moment(item.date).isSameOrAfter(moment().format("YYYY-MM-DD"))
    );

    if (selectedRadio === "Activity") {
      return (
        <SingleSelectDropdown
          classes={{
            formRoot: classes.dateRoot,
          }}
          menuOptions={availableDatesDropdown}
          selectedValue={selectedStartDate}
          showSubtitleText={true}
          onChange={(e) => onSelectStartDate(e.target.value)}
          error={errors.date}
          errorText="New date is required"
          dataTestId="date-dropdown"
          scrollToOptionIndex={scrollToOptionIndex}
          minwidth={1}
          placeholderText="Select Date"
          defaultText="Select an activity to see available dates"
        />
      );
    } else {
      return (
        <SingleSelectCalendar
          availableDates={availableDatesCalendar}
          defaultSelectedDate={selectedStartDate}
          onChange={(value) => {
            onSelectStartDate(value);
          }}
          maxDate={maxDateForCalendar}
          minDate={minDateForCalendar}
          error={errors.date}
          errorText="Date is required"
          // Prevent the popover from rendering behind the full-screen modal
          disablePortal
        />
      );
    }
  };

  return (
    <>
      <Typography
        variant={isDesktopLayout ? "h2" : "h3"}
        color="textPrimary"
        className={classes.header}
      >
        Transfer To
      </Typography>
      <Grid container spacing={isDesktopLayout ? 0 : 4}>
        <Grid
          container
          item
          direction={isDesktopLayout ? "column" : "row"}
          xs={isDesktopLayout ? 3 : 12}
          spacing={isDesktopLayout ? 0 : 3}
        >
          <Grid item>
            <Typography
              variant="h4"
              color="textPrimary"
              className={classes.searchBy}
            >
              Search by
            </Typography>
          </Grid>
          <Grid item>
            <RadioButton
              disableRipple
              label={"Activity"}
              value={"Activity"}
              onChange={switchRadio}
              checked={selectedRadio === "Activity"}
              classes={{ root: classes.radioButton }}
            />
          </Grid>
          <Grid item>
            <RadioButton
              disableRipple
              label={"Date"}
              value={"Date"}
              onChange={switchRadio}
              checked={selectedRadio === "Date"}
              classes={{ root: classes.radioButton }}
            />
          </Grid>
        </Grid>
        <Grid item xs={isDesktopLayout ? 9 : 12}>
          <Grid container direction="column" spacing={isDesktopLayout ? 6 : 4}>
            <Grid item>
              <Grid
                container
                direction={
                  selectedRadio === "Activity" ? "column" : "column-reverse"
                }
                spacing={isDesktopLayout ? 6 : 4}
              >
                <Grid item>
                  <SelectActivity
                    options={activities}
                    disabled={
                      selectedRadio === "Date" && selectedStartDate === ""
                    }
                    handleActivityChange={handleActivityChange}
                    selectedActivityId={activitySessionGroupId}
                    onOptionMouseLeave={onOptionMouseLeave}
                    onOptionMouseEnter={onOptionMouseEnter}
                    error={errors.activity}
                    loadingActivities={loadingActivities}
                  />

                  {selectedRadio === "Date" &&
                    activitySessionGroupId &&
                    showCapacity()}
                </Grid>
                <Grid item>
                  <Typography
                    variant="h4"
                    color="textPrimary"
                    className={
                      searchBy === "Activity"
                        ? classes.titleWithSubtitle
                        : classes.title
                    }
                  >
                    Select New Date
                  </Typography>
                  {searchBy === "Activity" && (
                    <Typography
                      variant="subtitle1"
                      color="textSecondary"
                      className={classes.subTitle}
                    >
                      {`The ${terminology.nonClientStudent.toLowerCase()}'s current registered dates will not appear in
                      the dropdown.`}
                    </Typography>
                  )}
                  {createDateSelector()}
                </Grid>
              </Grid>
              {studentAlreadyBooked && <StudentAlreadyBookedAlert />}
            </Grid>
            {featureFlags.v2BookingManagementTimeline && (
              <Grid item>
                <Notes
                  onBlur={(e) => {
                    onNotesChange(e.target.value);
                  }}
                  value={internalNotes}
                  error={errors.notes}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

SingleDayEnrollmentForm.defaultProps = {
  onOptionMouseLeave: () => {},
  onOptionMouseEnter: () => {},
  onSelectActivitySessionGroupId: () => {},
  activitySessionGroupId: undefined,
  onSelectStartDate: () => {},
  selectedStartDate: "",
  onNotesChange: () => {},
  internalNotes: "",
  errors: {},
  setErrors: () => {},
  loadingActivities: false,
};

SingleDayEnrollmentForm.propTypes = {
  classes: PropTypes.object.isRequired,
  activities: PropTypes.array.isRequired,
  availableDatesDropdown: PropTypes.array.isRequired,
  availableDatesCalendar: PropTypes.array.isRequired,
  onSelectActivitySessionGroupId: PropTypes.func,
  activitySessionGroupId: PropTypes.number,
  onSelectStartDate: PropTypes.func,
  selectedStartDate: PropTypes.string,
  onNotesChange: PropTypes.func,
  maxDateForCalendar: PropTypes.string,
  minDateForCalendar: PropTypes.string,
  onOptionMouseLeave: PropTypes.func,
  onOptionMouseEnter: PropTypes.func,
  internalNotes: PropTypes.string,
  errors: PropTypes.object,
  setErrors: PropTypes.func,
  loadingActivities: PropTypes.bool,
  studentAlreadyBooked: PropTypes.bool,
  terminology: PropTypes.object,
};

export default withStyles(styles)(SingleDayEnrollmentForm);
